<template>
    <div class="s-main-content" :class="className">
        <a-label v-if="label?.text" v-bind="label">
            {{ label.text }}
        </a-label>
        <a-dangerous-html class="title" tag="h1" :content="title" />
        <a-dangerous-html
            v-if="subtitle"
            class="subtitle"
            tag="p"
            :content="subtitle"
        />
        <a-dangerous-html
            v-if="description"
            class="description"
            tag="p"
            :content="description"
        />
        <div v-if="CTAisShown" class="cta-block">
            <a-product-cta
                v-if="ctaSettings"
                :settings="ctaSettings"
                use-new-label-design
            />
            <div v-if="buttons.length" class="buttons">
                <a-button
                    v-for="(button, i) in buttons"
                    :key="`button-${i}`"
                    v-modal="isDesktop && button.modal || null"
                    v-bind="button"
                    :size="button.size || 'small'"
                    :type="button.type || getButtonType(buttons, i)"
                    :event="{ ...event, ...getButtonGA(button) }"
                    @click="logInteraction(button)"
                />
            </div>
            <div v-if="actionLinks.length" class="action-links">
                <a-link
                    v-for="(link, i) in actionLinks"
                    :key="`link-${i}`"
                    v-bind="link"
                    :size="link.size || 'paragraph'"
                    :type="theme === 'light' ? 'regular' : 'light'"
                />
            </div>
            <div v-if="additionalLabel" class="cta-additional-label">
                <span v-html="additionalLabel" />
            </div>
        </div>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import AProductCta from '@core/components/product-cta/product-cta.vue';
import mixin from '../mixin.js';

export default {
    name: 'SMainContent',

    components: {
        AButton,
        ALink,
        ALabel,
        AProductCta,
        ADangerousHtml,
    },

    mixins: [mixin],

    props: {
        title: {
            type: String,
            required: true,
        },

        subtitle: {
            type: String,
            default: null,
        },

        centerAlign: {
            type: Boolean,
            default: false,
        },

        description: {
            type: String,
            default: null,
        },

        label: {
            type: Object,
            default: null,
        },

        productData: {
            type: Object,
            required: true,
        },

        ctaSettings: {
            type: Object,
            default: null,
        },

        buttons: {
            type: Array,
            default: () => [],
        },

        actionLinks: {
            type: Array,
            default: () => [],
        },

        additionalLabel: {
            type: String,
            default: null,
        },

        event: {
            type: Object,
            default: () => ({
                category: 'Conversions',
                location: 'Top CTA',
            }),
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'dark',
        },
    },

    computed: {
        className() {
            return {
                'light': this.theme === 'light',
                'center-align': this.centerAlign,
            };
        },

        CTAisShown() {
            return Boolean(
                this.buttons.length || this.actionLinks.length || this.ctaSettings || this.additionalLabel,
            );
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-main-content {
    flex-grow: 1;
    width: 100%;
    position: relative;

    &.center-align {
        text-align: center;

        &:deep(.cta-description) {
            justify-content: center;
        }

        .action-links {
            justify-content: center;
        }
    }

    &.light {
        .cta-block {
            &:deep(.cta) {
                .cta-description {
                    color: var(--av-nav-primary);

                    del {
                        color: var(--av-fixed-light);
                    }
                }
            }
        }
        .buttons {
            .a-button.a-button_type_secondary {
                border-color: var(--av-button-secondary);
                color: var(--av-link-primary);
            }
        }
    }
}

.title {
    @mixin display;

    @media (--viewport-mobile-wide) {
        @mixin hero;
    }

    @media (--viewport-desktop) {
        padding-inline-end: 32px;
    }

    @media (--viewport-desktop-wide) {
        @mixin large;
    }
}

.subtitle {
    @mixin lead;
    margin-top: 16px;

    @media (--viewport-mobile-wide) {
        @mixin title;
    }

    @media (--viewport-desktop) {
        padding-inline-end: 32px;
    }

}

.description {
    margin-top: 16px;
}

.cta-block {
    margin-top: 40px;

    &:deep(.cta) {
        margin-top: -8px;
        margin-bottom: 16px;

        .cta-description {
            @mixin title;
            margin: 8px 0 0;
            color: var(--av-inversed-primary);

            del {
                color: var(--av-inversed-light);
            }
        }
    }

    &:deep(.a-button) {
        min-width: 114px;
    }
}

.buttons {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    @media (--viewport-mobile-wide) {
        display: inline-grid;
        grid-auto-columns: 1fr;
        grid-column-gap: 16px;
    }

    .a-button {
        width: 100%;
        margin-bottom: 16px;
        padding: 11px 15px;
        color: var(--av-inversed-primary);

        @media (--viewport-mobile-wide) {
            grid-row: 1;
            margin-bottom: 0;
        }
    }
}

.action-links {
    margin-top: 16px;
    display: flex;
    justify-content: flex-start;

    .a-link {
        @mixin paragraph;
        color: var(--av-brand-secondary);
        text-decoration: none;
        margin-inline-end: 24px;
    }
}
</style>
